import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';

import PerfectScrollbar from 'react-perfect-scrollbar';

import MainCard from '../../../../ui-component/cards/MainCard';
import Transitions from '../../../../ui-component/extended/Transitions';
import NotificationList from './NotificationList';

import { IconBell, IconChecks } from '@tabler/icons';

import api from '../../../../views/services/api';

const POLL_INTERVAL = 30000;

const useStyles = makeStyles((theme) => ({
    headerAvatar: {
        ...theme.typography.commonAvatar,
        ...theme.typography.mediumAvatar,
        transition: 'all .2s ease-in-out',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        '&[aria-controls="menu-list-grow"],&:hover': {
            background: theme.palette.primary.main,
            color: '#fff'
        }
    },
    cardContent: {
        padding: '0px !important'
    },
    ScrollHeight: {
        height: '100%',
        maxHeight: 'calc(100vh - 250px)',
        overflowX: 'hidden'
    },
    box: {
        marginLeft: '16px',
        marginRight: '24px',
        [theme.breakpoints.down('sm')]: {
            marginRight: '16px'
        }
    },
    cardAction: {
        padding: '10px',
        justifyContent: 'space-between'
    }
}));

const NotificationSection = () => {
    const classes = useStyles();
    const theme = useTheme();
    const matchesXs = useMediaQuery(theme.breakpoints.down('sm'));

    const [open, setOpen] = useState(false);
    const [appId, setAppId] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const anchorRef = useRef(null);
    const pollRef = useRef(null);

    // ----- app padrão -----
    useEffect(() => {
        api.get('v1/apps/default/')
            .then((res) => setAppId(res.data?.app?.id || null))
            .catch(() => {});
    }, []);

    // ----- fetch notificações -----
    const fetchNotifications = useCallback(async () => {
        if (!appId) return;
        try {
            const res = await api.get(`v1/apps/${appId}/notifications/`);
            setNotifications(res.data?.notifications || []);
            setUnreadCount(res.data?.unread_count || 0);
        } catch {
            // silencia erros de background
        }
    }, [appId]);

    // primeiro fetch e polling
    useEffect(() => {
        if (!appId) return;
        setLoading(true);
        fetchNotifications().finally(() => setLoading(false));

        pollRef.current = setInterval(fetchNotifications, POLL_INTERVAL);
        return () => clearInterval(pollRef.current);
    }, [appId, fetchNotifications]);

    // refresh ao abrir dropdown
    useEffect(() => {
        if (open) fetchNotifications();
    }, [open, fetchNotifications]);

    // ----- ações -----
    const handleToggle = () => setOpen((prev) => !prev);

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) return;
        setOpen(false);
    };

    const handleMarkRead = async (notificationId) => {
        try {
            await api.patch(`v1/apps/${appId}/notifications/${notificationId}/`);
            setNotifications((prev) => prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n)));
            setUnreadCount((prev) => Math.max(0, prev - 1));
        } catch {
            // silencia
        }
    };

    const handleMarkAllRead = async () => {
        if (!appId || unreadCount === 0) return;
        try {
            await api.post(`v1/apps/${appId}/notifications/mark-all-read/`);
            setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
            setUnreadCount(0);
        } catch {
            // silencia
        }
    };

    // foco de acessibilidade no fechar
    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current?.focus();
        }
        prevOpen.current = open;
    }, [open]);

    const isDark = theme.palette.mode === 'dark';

    return (
        <>
            <Box component="span" className={classes.box}>
                <ButtonBase sx={{ borderRadius: '12px' }}>
                    <Badge
                        badgeContent={unreadCount > 0 ? unreadCount : null}
                        color="error"
                        max={99}
                        sx={{
                            '& .MuiBadge-badge': {
                                fontSize: 10,
                                height: 18,
                                minWidth: 18,
                                fontWeight: 700
                            }
                        }}
                    >
                        <Avatar
                            variant="rounded"
                            className={classes.headerAvatar}
                            ref={anchorRef}
                            aria-controls={open ? 'menu-list-grow' : undefined}
                            aria-haspopup="true"
                            onClick={handleToggle}
                        >
                            <IconBell stroke={1.5} size="1.3rem" />
                        </Avatar>
                    </Badge>
                </ButtonBase>
            </Box>

            <Popper
                placement={matchesXs ? 'bottom' : 'bottom-end'}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [{ name: 'offset', options: { offset: [matchesXs ? 5 : 0, 20] } }]
                }}
            >
                {({ TransitionProps }) => (
                    <Transitions in={open} {...TransitionProps}>
                        <Paper sx={{ minWidth: 340, maxWidth: 380, borderRadius: '16px', overflow: 'hidden' }}>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>

                                    {/* Header */}
                                    <CardContent className={classes.cardContent}>
                                        <Box
                                            sx={{
                                                px: 2,
                                                py: 1.5,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                background: isDark
                                                    ? 'linear-gradient(135deg,#111827,#1e293b)'
                                                    : 'linear-gradient(135deg,#eff6ff,#ffffff)',
                                                borderBottom: isDark
                                                    ? '1px solid rgba(255,255,255,0.06)'
                                                    : '1px solid rgba(15,23,42,0.06)'
                                            }}
                                        >
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <IconBell size={18} color={theme.palette.primary.main} />
                                                <Typography variant="subtitle1" fontWeight={700}>
                                                    Notificações
                                                </Typography>
                                                {unreadCount > 0 && (
                                                    <Box
                                                        sx={{
                                                            background: 'linear-gradient(135deg,#2563eb,#7c3aed)',
                                                            color: '#fff',
                                                            borderRadius: '10px',
                                                            px: 0.75,
                                                            py: 0.1,
                                                            fontSize: 11,
                                                            fontWeight: 800,
                                                            lineHeight: 1.6
                                                        }}
                                                    >
                                                        {unreadCount}
                                                    </Box>
                                                )}
                                            </Stack>

                                            {unreadCount > 0 && (
                                                <Tooltip title="Marcar tudo como lido">
                                                    <Box
                                                        onClick={handleMarkAllRead}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                            color: theme.palette.primary.main,
                                                            fontSize: 12,
                                                            fontWeight: 600,
                                                            '&:hover': { opacity: 0.75 }
                                                        }}
                                                    >
                                                        <IconChecks size={14} />
                                                        Ler tudo
                                                    </Box>
                                                </Tooltip>
                                            )}
                                        </Box>

                                        {/* Lista */}
                                        <PerfectScrollbar className={classes.ScrollHeight}>
                                            <NotificationList
                                                notifications={notifications.slice(0, 10)}
                                                onMarkRead={handleMarkRead}
                                                loading={loading}
                                            />
                                        </PerfectScrollbar>
                                    </CardContent>

                                    <Divider />

                                    {/* Footer */}
                                    <CardActions className={classes.cardAction}>
                                        <Typography
                                            component={Link}
                                            to="/dashboard/admin/notifications"
                                            variant="subtitle2"
                                            color="primary"
                                            onClick={() => setOpen(false)}
                                            sx={{
                                                textDecoration: 'none',
                                                fontWeight: 700,
                                                fontSize: 13,
                                                '&:hover': { textDecoration: 'underline' }
                                            }}
                                        >
                                            Ver todas as notificações →
                                        </Typography>

                                        {notifications.length > 0 && (
                                            <Typography variant="caption" color="text.secondary">
                                                {notifications.length} no total
                                            </Typography>
                                        )}
                                    </CardActions>

                                </MainCard>
                            </ClickAwayListener>
                        </Paper>
                    </Transitions>
                )}
            </Popper>
        </>
    );
};

export default NotificationSection;
